import { Link } from 'react-router-dom';
import ImageGallery from '../../components/ImageGallery';
import ProductCTA from '../../components/ProductCTA';
import { TECHNICAL_SPECS, VALUE_PROPOSITIONS } from '../../constants/specifications';
import './Tooling.css';

// Import tooling images
import tooling1 from '../../assets/products/tooling/tooling-pic1-enlarge.jpg';
import tooling2 from '../../assets/products/tooling/tooling-pic2-enlarge.jpg';
import tooling3 from '../../assets/products/tooling/tooling-pic3-enlarge.jpg';
import tooling4 from '../../assets/products/tooling/tooling-pic4b-enlarge.jpg';
import tooling5 from '../../assets/products/tooling/tooling-pic5-enlarge.jpg';
import tooling6 from '../../assets/products/tooling/tooling-pic6-enlarge.jpg';

const Tooling = () => {
  const toolingImages = [
    { src: tooling1, alt: 'Custom Roll Tooling Set 1', title: 'Precision Roll Tooling for Industrial Applications' },
    { src: tooling2, alt: 'Custom Roll Tooling Set 2', title: 'Multi-Pass Tooling Configuration' },
    { src: tooling3, alt: 'Custom Roll Tooling Set 3', title: 'Complex Profile Tooling Design' },
    { src: tooling4, alt: 'Custom Roll Tooling Set 4', title: 'Heavy-Duty Tooling for Thick Materials' },
    { src: tooling5, alt: 'Custom Roll Tooling Set 5', title: 'Specialized Automotive Tooling' },
    { src: tooling6, alt: 'Custom Roll Tooling Set 6', title: 'Construction Industry Tooling Solutions' },
  ];


  const capabilities = [
    { feature: 'Material Thickness Range', spec: `${TECHNICAL_SPECS.materialThickness.range} thickness capability` },
    { feature: 'Material Strength', spec: `${TECHNICAL_SPECS.materialStrength.description} materials` },
    { feature: 'Tooling Precision', spec: TECHNICAL_SPECS.technology.precision },
    { feature: 'Profile Complexity', spec: 'Simple to highly complex multi-bend profiles' },
    { feature: 'Production Volume', spec: 'Prototype to high-volume production runs' },
    { feature: 'Design Technology', spec: '3D CAD/CAM design and simulation' },
    { feature: 'Material Types', spec: 'Steel, aluminum, stainless steel, specialty alloys' },
    { feature: 'Quality Assurance', spec: 'In-house testing and validation' },
  ];

  const services = [
    {
      title: 'Custom Tooling Design',
      description: 'Complete tooling design from concept to production for all industries.',
      features: ['3D CAD modeling', 'Finite element analysis', 'Prototype development', 'Design optimization'],
      industries: ['Automotive', 'Construction', 'Transportation', 'Electronics']
    },
    {
      title: 'Tooling Manufacturing',
      description: 'In-house tooling fabrication with precision machining capabilities.',
      features: ['CNC machining', 'Heat treatment', 'Surface finishing', 'Quality inspection'],
      industries: ['Architecture', 'HVAC', 'Appliance Mfg']
    },
    {
      title: 'Tool Proving & Testing',
      description: 'Comprehensive testing to ensure optimal performance across applications.',
      features: ['First article inspection', 'Production trials', 'Quality validation', 'Performance optimization'],
      industries: ['All Industries', 'Prototype to Production']
    },
    {
      title: 'Maintenance & Support',
      description: 'Ongoing support to maximize tooling life and performance.',
      features: ['Preventive maintenance', 'Repair services', 'Performance monitoring', 'Upgrade consulting'],
      industries: ['Lifecycle Support', 'All Applications']
    }
  ];

  return (
    <div className="tooling-page">
      <div className="container">
        {/* Breadcrumb */}
        <nav className="breadcrumb">
          <Link to="/">Home</Link> / <Link to="/products">Products</Link> / Tooling & Dies
        </nav>

        {/* Page Header */}
        <div className="page-header">
          <h1>Custom Tooling & Dies</h1>
          <p className="page-subtitle">
            Precision-engineered tooling solutions for every roll forming application
          </p>
        </div>

        {/* Overview Section */}
        <section className="overview-section">
          <div className="overview-content">
            <h2>Engineering Excellence in Tooling Design</h2>
            <p>
              Our custom tooling and dies are the heart of every successful roll forming operation. 
              With {VALUE_PROPOSITIONS.experience.toLowerCase()}, we design and manufacture precision tooling that 
              delivers consistent quality, exceptional performance, and long service life.
            </p>
            <p>
              Using the latest {TECHNICAL_SPECS.technology.design}, we create tooling solutions for simple to 
              highly complex profiles across diverse industries. Every tool set is engineered 
              to your exact specifications and rigorously tested to ensure optimal performance.
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="services-section">
          <h2>Tooling Services & Capabilities</h2>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-card">
                <h3>{service.title}</h3>
                <p>{service.description}</p>
                <div className="service-features">
                  <h4>Key Features:</h4>
                  <ul>
                    {service.features.map((feature, idx) => (
                      <li key={idx}>{feature}</li>
                    ))}
                  </ul>
                </div>
                <div className="service-industries">
                  <h4>Industries Served:</h4>
                  <div className="industry-tags">
                    {service.industries.map((industry, idx) => (
                      <span key={idx} className="industry-tag">{industry}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Technical Capabilities */}
        <section className="capabilities-section">
          <h2>Technical Capabilities</h2>
          <div className="capabilities-table-wrapper">
            <table className="capabilities-table">
              <thead>
                <tr>
                  <th>Capability</th>
                  <th>Specification</th>
                </tr>
              </thead>
              <tbody>
                {capabilities.map((cap, index) => (
                  <tr key={index}>
                    <td>{cap.feature}</td>
                    <td>{cap.spec}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Process Overview */}
        <section className="process-section">
          <h2>Our Tooling Design Process</h2>
          <div className="process-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <h3>Consultation & Requirements</h3>
              <p>We work closely with you to understand your specific requirements, material specifications, and production goals.</p>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <h3>3D Design & Simulation</h3>
              <p>Our engineers create detailed 3D models and run simulations to optimize the tooling design for your application.</p>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <h3>Precision Manufacturing</h3>
              <p>Tools are manufactured in-house using advanced CNC machining and precision manufacturing techniques.</p>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <h3>Testing & Validation</h3>
              <p>Comprehensive testing ensures your tooling meets all specifications and performance requirements before delivery.</p>
            </div>
          </div>
        </section>

        {/* Image Gallery */}
        <section className="gallery-section">
          <h2>Tooling Portfolio</h2>
          <p>Explore examples of our custom tooling solutions across various industries and applications.</p>
          <ImageGallery images={toolingImages} columns={3} />
        </section>

        {/* CTA Section */}
        <ProductCTA 
          variant="engineering"
          title="Ready to Discuss Your Tooling Requirements?"
          description="Our tooling engineers are ready to work with you on your next project. From concept to production, we'll deliver the precision tooling solution you need."
          primaryButtonText="Request Tooling Quote"
          secondaryButtonText="Speak with Engineer"
        />
      </div>
    </div>
  );
};

export default Tooling;